import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Eye, EyeOff, Lock, Mail, Sparkles, User } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, LOGO_URL, useAuth, setStoredToken } from "@/lib/api";

const IMG_LEFT = "https://static.prod-images.emergentagent.com/jobs/6c3dbc7e-e3b1-4c5d-b777-4fea74d38e17/images/491fba79bbeeba116c7238b2b6351c9908792abddb2197f2309bf60557b65f8f.jpeg";

function PasswordRules({ value }) {
    const rules = [
        { ok: value.length >= 8, label: "8+ caractères" },
        { ok: /[A-Z]/.test(value), label: "1 majuscule" },
        { ok: /\d/.test(value), label: "1 chiffre" },
        { ok: /[^A-Za-z0-9]/.test(value), label: "1 spécial" },
    ];
    return (
        <ul className="mt-2 grid grid-cols-2 gap-1 text-[11px]">
            {rules.map((r) => (
                <li key={r.label} className={r.ok ? "text-green-700" : "text-brand-smoke"}>
                    {r.ok ? "✓" : "○"} {r.label}
                </li>
            ))}
        </ul>
    );
}

// IMPORTANT: Field MUST stay at module scope. Declaring it inside the Signup
// component function re-creates the component identity on every keystroke,
// which causes React to unmount/remount the underlying <input> and lose keyboard
// focus — a classic anti-pattern. Module scope = stable identity = no remount.
function Field({ label, ...rest }) {
    return (
        <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-brand-ink">{label}</label>
            <input className="mt-2 w-full rounded-xl border border-brand-line bg-white py-3 px-4 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20" {...rest} />
        </div>
    );
}


export default function Signup() {
    const { user, setUser } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        email: "",
        first_name: "",
        last_name: "",
        phone: "",
        password: "",
        confirm: "",
        adeli_rpps: "",
        accept_cgu: false,
    });
    const [show, setShow] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (user) navigate("/stagiaire", { replace: true });
    }, [user, navigate]);

    const submit = async (e) => {
        e.preventDefault();
        if (form.password !== form.confirm) {
            toast.error("Les mots de passe ne correspondent pas");
            return;
        }
        if (!form.accept_cgu) {
            toast.error("Vous devez accepter les CGU");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/auth/signup", {
                email: form.email.trim().toLowerCase(),
                password: form.password,
                first_name: form.first_name.trim(),
                last_name: form.last_name.trim(),
                phone: form.phone.trim() || null,
                adeli_rpps: form.adeli_rpps.trim() || null,
                accept_cgu: form.accept_cgu,
            });
            setStoredToken(data.access_token);
            setUser(data.user);
            toast.success(`Bienvenue, ${data.user.first_name} !`);
            navigate("/stagiaire", { replace: true });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2" data-testid="page-signup">
            {/* Left */}
            <div className="relative hidden overflow-hidden bg-brand-ink text-white lg:block">
                <img src={IMG_LEFT} alt="" className="absolute inset-0 h-full w-full object-cover opacity-65" />
                <div className="absolute inset-0 bg-gradient-to-br from-brand-ink via-brand-ink/80 to-brand-ink/85" />
                <div className="absolute inset-0 grain opacity-40" />
                <div className="relative flex h-full flex-col justify-between p-14">
                    <Link to="/" className="flex items-center gap-3" data-testid="signup-logo-link">
                        <img src={LOGO_URL} alt="Cursus Physio" className="h-10 w-auto brightness-0 invert" />
                    </Link>
                    <div>
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/[0.06] px-4 py-1.5 text-[11px] uppercase tracking-[0.28em] backdrop-blur"
                        >
                            <Sparkles className="h-3.5 w-3.5 text-brand-orange" />
                            Nouveau compte stagiaire
                        </motion.div>
                        <motion.h1
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.15 }}
                            className="mt-8 font-display text-5xl font-normal leading-[0.95] tracking-tightest lg:text-7xl"
                        >
                            Rejoignez la <em className="italic text-brand-orange">Prévention Active</em>.
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.3 }}
                            className="mt-8 max-w-md text-base text-white/70 leading-relaxed"
                        >
                            Créez votre compte pour suivre vos formations, accéder à vos documents et progresser à travers nos cursus.
                        </motion.p>
                    </div>
                    <div className="text-xs text-white/40">© {new Date().getFullYear()} Cursus Physio</div>
                </div>
            </div>

            {/* Right */}
            <div className="flex items-center justify-center bg-[#FAFAFA] px-6 py-16">
                <div className="w-full max-w-md">
                    <div className="mb-10 lg:hidden">
                        <img src={LOGO_URL} alt="Cursus Physio" className="h-10 w-auto" />
                    </div>
                    <h2 className="font-display text-5xl font-normal tracking-tightest">Créer un compte.</h2>
                    <p className="mt-3 text-brand-ink/60">Réservé aux kinésithérapeutes — accès gratuit, aucune carte bancaire.</p>

                    <form onSubmit={submit} className="mt-10 space-y-4" data-testid="signup-form">
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Prénom *" required value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} data-testid="signup-first-name" />
                            <Field label="Nom *" required value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} data-testid="signup-last-name" />
                        </div>
                        <Field label="Email *" type="email" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="signup-email" />
                        <Field label="Téléphone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="signup-phone" />
                        <div>
                            <Field label="Numéro ADELI / RPPS (optionnel)" value={form.adeli_rpps} onChange={(e) => setForm((f) => ({ ...f, adeli_rpps: e.target.value }))} data-testid="signup-adeli" placeholder="11 chiffres" />
                            <div className="mt-1.5 text-[11px] text-brand-smoke">
                                Votre numéro apparaîtra sur vos conventions et attestations pour simplifier les démarches <strong>FIF PL</strong> &amp; <strong>DPC</strong>.
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider">Mot de passe *</label>
                            <div className="relative mt-2">
                                <input
                                    type={show ? "text" : "password"}
                                    required
                                    value={form.password}
                                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                                    data-testid="signup-password"
                                    className="w-full rounded-xl border border-brand-line bg-white py-3 pl-4 pr-12 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                                />
                                <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-smoke">
                                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            <PasswordRules value={form.password} />
                        </div>
                        <Field label="Confirmer *" type={show ? "text" : "password"} required value={form.confirm} onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))} data-testid="signup-confirm" />

                        <label className="flex items-start gap-3 rounded-xl border border-brand-line bg-white p-4 text-sm">
                            <input type="checkbox" required checked={form.accept_cgu} onChange={(e) => setForm((f) => ({ ...f, accept_cgu: e.target.checked }))} data-testid="signup-cgu" className="mt-0.5 h-4 w-4 accent-brand-orange" />
                            <span className="text-brand-ink/85">
                                J'accepte les <Link to="/cgu" className="text-brand-orange hover:underline">Conditions Générales d'Utilisation</Link>
                            </span>
                        </label>

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="signup-submit"
                            className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-4 text-sm font-semibold text-white hover:bg-[#d9772c] disabled:opacity-60"
                        >
                            {submitting ? "Création…" : (<>Créer mon compte <ArrowRight className="h-4 w-4" /></>)}
                        </button>

                        <div className="text-center text-xs text-brand-smoke pt-3">
                            Déjà un compte ?{" "}
                            <Link to="/login" data-testid="signup-back-login" className="font-semibold text-brand-orange hover:underline">
                                Se connecter
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
